import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles,
  RefreshCw,
  FileText,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Activity,
} from "lucide-react";
import { toast } from "sonner";
import { weeklyUpdatesApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/* ─────────── shared health styling ─────────── */

const HEALTH = {
  on_track: { label: "On Track", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  at_risk:  { label: "At Risk",  cls: "bg-amber-500/15   text-amber-300   border-amber-500/40" },
  delayed:  { label: "Delayed",  cls: "bg-rose-500/15    text-rose-300    border-rose-500/40" },
};

/* ────────────────────────── page ────────────────────────── */

export default function AdminWeeklyUpdatesPage() {
  const [updates, setUpdates] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(""); // "" == latest
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const w = await weeklyUpdatesApi.listWeeks();
        setWeeks(w);
      } catch {
        /* non-fatal — the fallback list below still shows */
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await weeklyUpdatesApi.listAll(selectedWeek || undefined);
        // If no explicit week was picked, show only the latest week.
        if (!selectedWeek && data.length) {
          const latest = data[0].week_ending;
          setUpdates(data.filter((u) => u.week_ending === latest));
        } else {
          setUpdates(data);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedWeek]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await weeklyUpdatesApi.generateNow();
      toast.success(
        `Generated ${res.generated} update${res.generated === 1 ? "" : "s"}` +
          (res.skipped ? ` · ${res.skipped} skipped` : ""),
      );
      // Refresh the current week view
      const data = await weeklyUpdatesApi.listAll();
      const latest = data.length ? data[0].week_ending : "";
      setSelectedWeek("");
      setUpdates(data.filter((u) => !latest || u.week_ending === latest));
      // Refresh the week picker in case a new week landed
      const w = await weeklyUpdatesApi.listWeeks();
      setWeeks(w);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Failed to generate weekly updates",
      );
    } finally {
      setGenerating(false);
    }
  };

  const shownWeek = updates[0]?.week_ending || selectedWeek || "—";

  return (
    <div className="min-h-screen max-w-screen-2xl mx-auto px-6 lg:px-10 py-10">
      <div className="flex items-center gap-3 mb-2">
        <Sparkles className="w-5 h-5 text-amber-500" />
        <div className="text-overline text-zinc-500">Admin · AI Digest</div>
      </div>
      <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl font-light text-zinc-100">
            Weekly Updates
          </h1>
          <p className="text-sm text-zinc-500 mt-1" data-testid="weekly-updates-subtitle">
            {loading
              ? "Loading…"
              : `${updates.length} project update${updates.length === 1 ? "" : "s"} · week ending ${shownWeek}`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {weeks.length > 0 && (
            <Select
              value={selectedWeek || "__latest__"}
              onValueChange={(v) => setSelectedWeek(v === "__latest__" ? "" : v)}
            >
              <SelectTrigger
                data-testid="week-picker"
                className="w-52 bg-zinc-950 border-zinc-800 text-zinc-200"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-200">
                <SelectItem value="__latest__">Latest week</SelectItem>
                {weeks.map((w) => (
                  <SelectItem key={w} value={w}>
                    Week ending {w}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            data-testid="generate-now-btn"
            onClick={handleGenerate}
            disabled={generating}
            className="bg-amber-500 text-black hover:bg-amber-400 rounded-sm font-medium disabled:opacity-60"
          >
            <RefreshCw
              className={`w-4 h-4 mr-2 ${generating ? "animate-spin" : ""}`}
            />
            {generating ? "Generating…" : "Generate now"}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-zinc-500">Loading…</div>
      ) : updates.length === 0 ? (
        <EmptyState onGenerate={handleGenerate} generating={generating} />
      ) : (
        <div className="space-y-4">
          {updates.map((u) => (
            <UpdateCard key={u.id} update={u} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────────── update card (collapsible) ─────────────── */

function UpdateCard({ update }) {
  const [open, setOpen] = useState(false);
  const health = HEALTH[update.health_status] || HEALTH.on_track;
  const m = update.metrics || {};

  return (
    <div
      data-testid={`weekly-update-card-${update.project_id}`}
      className="border border-zinc-800 rounded-sm bg-zinc-950 overflow-hidden"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-4 p-5 text-left hover:bg-zinc-900/40 transition-colors"
      >
        {open ? (
          <ChevronDown className="w-4 h-4 text-zinc-500 shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-zinc-500 shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="font-heading text-xl text-zinc-100">
              {update.project_name}
            </div>
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-mono font-bold uppercase tracking-wider border ${health.cls}`}
            >
              {health.label}
            </span>
          </div>
          <div className="text-sm text-zinc-400 mt-1.5 line-clamp-2">
            {update.summary}
          </div>
          <div className="flex items-center gap-4 mt-2 text-[11px] font-mono text-zinc-500">
            <span>Done: {m.completed_this_week ?? 0}</span>
            <span>In progress: {m.in_progress ?? 0}</span>
            <span className={m.missed_deadlines ? "text-rose-400" : ""}>
              Missed: {m.missed_deadlines ?? 0}
            </span>
            <span>Upcoming: {m.due_next_7_days ?? 0}</span>
          </div>
        </div>
        <Link
          to={`/projects/${update.project_id}`}
          onClick={(e) => e.stopPropagation()}
          data-testid={`weekly-update-open-project-${update.project_id}`}
          className="text-zinc-500 hover:text-amber-500 transition-colors shrink-0"
          title="Open project"
        >
          <ExternalLink className="w-4 h-4" />
        </Link>
      </button>
      {open && (
        <div className="border-t border-zinc-800 p-5 pl-14 whitespace-pre-line text-sm leading-relaxed text-zinc-300">
          {update.body}
          {m.next_milestone && (
            <div className="mt-4 pt-4 border-t border-zinc-900 flex items-center gap-2 text-xs text-zinc-500 font-mono">
              <Activity className="w-3 h-3" />
              Next milestone:{" "}
              <span className="text-zinc-300">{m.next_milestone.name}</span>
              {m.next_milestone.start && (
                <span>· {m.next_milestone.start}</span>
              )}
            </div>
          )}
          <div className="mt-2 text-[10px] font-mono text-zinc-600 uppercase tracking-wider">
            Generated {update.generated_at?.slice(0, 16).replace("T", " ")}
            {update.generated_by === "system" ? " · auto" : " · manual"}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────── empty state ─────────────────────── */

function EmptyState({ onGenerate, generating }) {
  return (
    <div className="border border-dashed border-zinc-800 rounded-md p-16 text-center">
      <FileText className="w-8 h-8 text-zinc-700 mx-auto mb-4" />
      <div className="font-heading text-xl text-zinc-300 mb-2">
        No updates yet
      </div>
      <p className="text-sm text-zinc-500 max-w-md mx-auto mb-6">
        Weekly updates generate automatically every Monday at 8am. You can also
        generate this week&apos;s batch on demand.
      </p>
      <Button
        onClick={onGenerate}
        disabled={generating}
        className="bg-amber-500 text-black hover:bg-amber-400 rounded-sm"
      >
        <RefreshCw
          className={`w-4 h-4 mr-2 ${generating ? "animate-spin" : ""}`}
        />
        Generate this week&apos;s updates
      </Button>
    </div>
  );
}
